// Mock data for demo purposes

import { User, LoginCredentials, RegisterData } from './authService';
import { Course, CourseWithLessons, Lesson, UserProgress, CourseListResponse, LessonWithProgress, CourseFilters } from './courseService';
import { INTRO_ML_LESSONS, FIRST_MODEL_LESSONS, DATA_FUNDAMENTALS_LESSONS } from './mockDataExpanded';

// Mock users for demo
const MOCK_USER: User = {
  id: '1',
  email: 'demo@robolearn.com',
  username: 'demo_user',
  is_active: true,
  is_verified: true,
  created_at: '2024-01-15T10:30:00Z'
};

// Mock courses with compelling ML content
export const MOCK_COURSES: Course[] = [
  {
    id: '1',
    title: 'Introduction to Machine Learning',
    description: 'Start your machine learning journey with this comprehensive introduction course. Perfect for absolute beginners with no coding background. Learn what machine learning is, how it works, and where it\'s used in the real world.',
    short_description: 'Learn the fundamentals of ML with interactive exercises and real-world examples.',
    difficulty_level: 'beginner',
    status: 'published',
    estimated_duration_hours: 8,
    cover_image_url: null,
    tags: ['machine-learning', 'beginner', 'fundamentals', 'ai'],
    prerequisites: [],
    learning_objectives: [
      'Understand what machine learning is and how it differs from traditional programming',
      'Learn about different types of machine learning: supervised, unsupervised, and reinforcement',
      'Recognize real-world applications of machine learning',
      'Understand the machine learning workflow from data to deployment',
      'Learn key terminology and concepts used in the field'
    ],
    enrollment_count: 1247,
    average_rating: 4.8,
    is_featured: true,
    is_free: true,
    created_at: '2024-01-10T09:00:00Z',
    updated_at: '2024-03-15T14:30:00Z'
  },
  {
    id: '2', 
    title: 'Your First ML Model',
    description: 'Build your very first machine learning model from start to finish! This hands-on course teaches you to create a simple prediction model using visual tools and minimal coding. You\'ll learn to prepare data, train a model, and make predictions.',
    short_description: 'Create your first prediction model with guided, hands-on exercises.',
    difficulty_level: 'beginner',
    status: 'published',
    estimated_duration_hours: 12,
    cover_image_url: null,
    tags: ['hands-on', 'model-building', 'prediction', 'practical'],
    prerequisites: ['Introduction to Machine Learning'],
    learning_objectives: [
      'Prepare data for machine learning using simple tools',
      'Build and train your first classification model',
      'Evaluate model performance and understand accuracy metrics',
      'Make predictions with your trained model',
      'Deploy a simple model for others to use'
    ],
    enrollment_count: 892,
    average_rating: 4.7,
    is_featured: true,
    is_free: false,
    created_at: '2024-01-20T11:00:00Z',
    updated_at: '2024-03-10T16:20:00Z'
  },
  {
    id: '3',
    title: 'Data Fundamentals for ML',
    description: 'Master the art of working with data - the fuel of machine learning! Learn to find, clean, explore, and prepare data for machine learning projects. This course focuses on practical skills using visual tools and interactive exercises.',
    short_description: 'Learn to find, clean, and prepare data for machine learning projects.',
    difficulty_level: 'intermediate',
    status: 'published',
    estimated_duration_hours: 15,
    cover_image_url: null,
    tags: ['data-science', 'data-cleaning', 'statistics', 'visualization'],
    prerequisites: ['Introduction to Machine Learning'],
    learning_objectives: [
      'Find and evaluate data sources for ML projects',
      'Clean messy data and handle missing values',
      'Explore data using visualizations and statistics',
      'Transform data for better ML performance',
      'Understand data quality and bias issues'
    ],
    enrollment_count: 654,
    average_rating: 4.6,
    is_featured: false,
    is_free: false,
    created_at: '2024-02-01T13:30:00Z',
    updated_at: '2024-03-12T10:45:00Z'
  }
];

// Mock user progress with course details
const MOCK_PROGRESS: (UserProgress & { courseTitle?: string })[] = [
  {
    id: 'p1',
    user_id: '1',
    course_id: '1',
    courseTitle: 'Introduction to Machine Learning',
    is_enrolled: true,
    is_completed: false,
    completion_percentage: 65,
    total_lessons: 6,
    completed_lessons: 4,
    total_points_earned: 57,
    total_time_spent_minutes: 78,
    enrolled_at: '2024-03-10T09:00:00Z',
    last_accessed_at: '2024-03-20T15:30:00Z',
    completed_at: null,
    current_lesson_id: '1-5'
  },
  {
    id: 'p2',
    user_id: '1',
    course_id: '2',
    courseTitle: 'Your First ML Model',
    is_enrolled: true,
    is_completed: false,
    completion_percentage: 20,
    total_lessons: 8,
    completed_lessons: 1,
    total_points_earned: 12,
    total_time_spent_minutes: 25,
    enrolled_at: '2024-03-15T14:00:00Z',
    last_accessed_at: '2024-03-16T10:20:00Z',
    completed_at: null,
    current_lesson_id: '2-2'
  }
];

// Helper function to simulate network delay
const simulateDelay = (ms: number = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Mock auth service
export const mockAuthService = {
  async login(credentials: LoginCredentials): Promise<User> {
    await simulateDelay();
    
    // Simple demo validation - any email/password works
    if (credentials.email && credentials.password) {
      // Simulate storing a token
      localStorage.setItem('robolearn_access_token', 'mock_token_12345');
      return MOCK_USER;
    }
    
    throw new Error('Invalid credentials');
  },

  async register(data: RegisterData): Promise<User> {
    await simulateDelay();
    
    // Simulate successful registration
    return {
      ...MOCK_USER,
      email: data.email,
      username: data.username,
      id: 'new_user_' + Date.now()
    };
  },

  async getCurrentUser(): Promise<User> {
    await simulateDelay(200);
    
    // Check if user has a token
    const token = localStorage.getItem('robolearn_access_token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    return MOCK_USER;
  },

  logout(): void {
    localStorage.removeItem('robolearn_access_token');
  },

  async verifyEmail(token: string): Promise<void> {
    await simulateDelay();
    // Simulate successful verification
  },

  async deactivateAccount(): Promise<void> {
    await simulateDelay();
    localStorage.removeItem('robolearn_access_token');
  }
};

// Mock course service
export const mockCourseService = {
  async getCourses(filters: CourseFilters = {}): Promise<CourseListResponse> {
    await simulateDelay();
    
    return {
      courses: MOCK_COURSES,
      total: MOCK_COURSES.length,
      page: 1,
      page_size: 10
    };
  },

  async getFeaturedCourses(): Promise<Course[]> {
    await simulateDelay();
    return MOCK_COURSES.filter(course => course.is_featured);
  },

  async searchCourses(query: string, page: number = 1, pageSize: number = 10): Promise<CourseListResponse> {
    await simulateDelay();
    
    const filteredCourses = MOCK_COURSES.filter(course => 
      course.title.toLowerCase().includes(query.toLowerCase()) ||
      course.description.toLowerCase().includes(query.toLowerCase()) ||
      course.tags.some(tag => tag.toLowerCase().includes(query.toLowerCase()))
    );
    
    return {
      courses: filteredCourses,
      total: filteredCourses.length,
      page: 1,
      page_size: 10
    };
  },

  async getCourse(courseId: string): Promise<CourseWithLessons> {
    await simulateDelay();
    
    const course = MOCK_COURSES.find(c => c.id === courseId);
    if (!course) {
      throw new Error('Course not found');
    }
    
    let lessons: Lesson[] = [];
    switch (courseId) {
      case '1':
        lessons = INTRO_ML_LESSONS;
        break;
      case '2':
        lessons = FIRST_MODEL_LESSONS;
        break;
      case '3':
        lessons = DATA_FUNDAMENTALS_LESSONS;
        break;
      default:
        lessons = [];
    }
    
    return {
      ...course,
      lessons
    };
  },

  async getCourseLessons(courseId: string): Promise<Lesson[]> {
    await simulateDelay();
    
    switch (courseId) {
      case '1':
        return INTRO_ML_LESSONS;
      case '2':
        return FIRST_MODEL_LESSONS;
      case '3':
        return DATA_FUNDAMENTALS_LESSONS;
      default:
        return [];
    }
  },

  async getLesson(lessonId: string): Promise<Lesson> {
    await simulateDelay();
    
    // Search across all lesson arrays
    const allLessons = [...INTRO_ML_LESSONS, ...FIRST_MODEL_LESSONS, ...DATA_FUNDAMENTALS_LESSONS];
    const lesson = allLessons.find(l => l.id === lessonId);
    
    if (!lesson) {
      throw new Error('Lesson not found');
    }
    
    return lesson;
  },

  async enrollInCourse(courseId: string): Promise<UserProgress> {
    await simulateDelay();
    
    const course = MOCK_COURSES.find(c => c.id === courseId);
    if (!course) {
      throw new Error('Course not found');
    }
    
    const newProgress: UserProgress = {
      id: 'p_' + Date.now(),
      user_id: '1',
      course_id: courseId,
      is_enrolled: true,
      is_completed: false,
      completion_percentage: 0,
      total_lessons: courseId === '1' ? 6 : 8,
      completed_lessons: 0,
      total_points_earned: 0,
      total_time_spent_minutes: 0,
      enrolled_at: new Date().toISOString(),
      last_accessed_at: null,
      completed_at: null,
      current_lesson_id: null
    };
    
    return newProgress;
  },

  async getCourseProgress(courseId: string): Promise<UserProgress> {
    await simulateDelay();
    
    const progress = MOCK_PROGRESS.find(p => p.course_id === courseId);
    if (!progress) {
      throw new Error('Progress not found');
    }
    
    return progress;
  },

  async getUserEnrollments(): Promise<UserProgress[]> {
    await simulateDelay();
    return MOCK_PROGRESS;
  },

  async startLesson(lessonId: string): Promise<void> {
    await simulateDelay();
    // Simulate starting lesson
  },

  async updateLessonProgress(lessonId: string, data: any): Promise<void> {
    await simulateDelay();
    // Simulate updating progress
  },

  async getCourseLessonsWithProgress(courseId: string): Promise<LessonWithProgress[]> {
    await simulateDelay();
    
    const lessons = await this.getCourseLessons(courseId);
    const progress = MOCK_PROGRESS.find(p => p.course_id === courseId);
    
    return lessons.map((lesson, index) => ({
      ...lesson,
      is_completed: progress ? index < progress.completed_lessons : false,
      is_started: progress ? index <= progress.completed_lessons : false,
      completion_percentage: progress && index < progress.completed_lessons ? 100 : 
                           progress && index === progress.completed_lessons ? 45 : 0,
      points_earned: progress && index < progress.completed_lessons ? lesson.points_value : 0,
      time_spent_minutes: progress && index < progress.completed_lessons ? lesson.estimated_duration_minutes : 0
    }));
  }
};